import { Avatar, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { LinkItem } from "LinkItem";
import { OLPaper } from "OLPaper";
import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import ChangeRedemptionStatusModal from "components/Tooltips/ChangeRedemptionStatusModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useIsOverflown } from "hooks/useIsOverflown";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useParams } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { SINGLE_REWARD_FULFILLMENTS_ID } from "state/tables/types";
import { ListResponse } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatNumberValue } from "utilities/numbers";
import { getAcronym, pickPluralizedUnit } from "utilities/strings";
import { RewardFulfillmentAddressCard } from "./RewardFulfillmentAddressCard";
import { useStyles } from "./SingleRewardFulfillment.styles";

function CreateItems(items: any[]): Row[] {
  const { message } = useMessage();
  return items.map(
    (
      item: { comment: string; status: string; updatedAt?: string | Date },
      index: number,
    ) => ({
      cells: [
        { content: message({ id: item.status }), property: "status" },
        { content: item.comment, property: "comment" },
        { content: OLDate(item.updatedAt), property: "updatedAt" },
      ],
      id: `${index}`,
    }),
  );
}

export function SingleRewardFulfillment() {
  const classes = useStyles();
  const { message } = useMessage();
  const { listing } = useListing();
  const { id: issuedRewardId } = useParams<{ id: string }>();
  const [open, setOpen] = useState(false);
  const { setColumnOrdering } = useTablesStateHelpers();
  const { observeElement, isOverflown } = useIsOverflown();
  const { useRedemptionHistory, useRedemptionDetails } = useReward();
  const { data: redemptionHistory } = useRedemptionHistory(
    issuedRewardId as string,
    {
      order: listing.order || "desc",
      page: listing.page,
      rows: 50,
      sortKey: listing.property || "updatedAt",
    },
  ) as { data: ListResponse };

  const { data: redemptionDetails } = useRedemptionDetails(
    issuedRewardId as string,
  );

  const { useRewardData } = useReward();
  const { data: rewardData } = useRewardData(redemptionDetails?.rewardId);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      headerText: message({ id: "comment" }),
      property: "comment",
      isSortable: false,
    },
    {
      headerText: message({ id: "updatedAt" }),
      property: "updatedAt",
      isSortable: false,
    },
  ]);

  const items = redemptionHistory ? CreateItems(redemptionHistory.items) : [];

  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const details = [
    {
      label: "firstName",
      value: redemptionDetails?.customerId ? (
        <LinkItem
          to={links.singleMemberGeneralInfo(redemptionDetails?.customerId)}
          withHover
        >
          {redemptionDetails?.customerData?.firstName}
        </LinkItem>
      ) : (
        redemptionDetails?.customerData?.firstName
      ),
      dataHjSuppress: true,
    },
    {
      label: "lastName",
      value: redemptionDetails?.customerId ? (
        <LinkItem
          to={links.singleMemberGeneralInfo(redemptionDetails?.customerId)}
          withHover
        >
          {redemptionDetails?.customerData?.lastName}
        </LinkItem>
      ) : (
        redemptionDetails?.customerData?.lastName
      ),
      dataHjSuppress: true,
    },
    {
      label: "email",
      value: redemptionDetails?.customerId ? (
        <LinkItem
          to={links.singleMemberGeneralInfo(redemptionDetails?.customerId)}
          withHover
        >
          {redemptionDetails?.customerData?.email}
        </LinkItem>
      ) : (
        redemptionDetails?.customerData?.email
      ),
      dataHjSuppress: true,
    },
    {
      label: "phoneNumber",
      value: redemptionDetails?.customerId ? (
        <LinkItem
          to={links.singleMemberGeneralInfo(redemptionDetails?.customerId)}
          withHover
        >
          {redemptionDetails?.customerData?.phone}
        </LinkItem>
      ) : (
        redemptionDetails?.customerData?.phone
      ),
      dataHjSuppress: true,
    },
    {
      label: "memberID",
      value: redemptionDetails?.customerId ? (
        <LinkItem
          to={links.singleMemberGeneralInfo(redemptionDetails?.customerId)}
          withHover
        >
          {redemptionDetails?.customerId}
        </LinkItem>
      ) : (
        redemptionDetails?.customerId
      ),
      dataHjSuppress: true,
    },
    {
      label: "rewardId",
      value: redemptionDetails?.rewardId ? (
        <LinkItem to={links.reward(redemptionDetails?.rewardId)} withHover>
          {redemptionDetails?.rewardId}
        </LinkItem>
      ) : (
        redemptionDetails?.rewardId
      ),
    },
    {
      label: "redemptionDate",
      value: OLDate(redemptionDetails?.redemptionDate),
    },
    {
      label: "priceInPoints",
      value: redemptionDetails?.costInPoints
        ? `${formatNumberValue(redemptionDetails?.costInPoints)} ${pickPluralizedUnit(
            redemptionDetails?.costInPoints,
            defaultWallet?.unitSingularName,
            defaultWallet?.unitPluralName,
          )}`
        : "-",
    },
    ...(redemptionDetails?.issuedCoupon
      ? [
          {
            label: "couponCode",
            value: redemptionDetails?.issuedCoupon?.code,
          },
          {
            label: "couponValue",
            value: redemptionDetails?.issuedCoupon?.value,
          },
          {
            label: "activeFrom",
            value: OLDate(redemptionDetails?.issuedCoupon?.activeFrom),
          },
          {
            label: "activeTo",
            value: OLDate(redemptionDetails?.issuedCoupon?.activeTo),
          },
          {
            label: "usageDate",
            value: OLDate(redemptionDetails?.issuedCoupon?.usedAt),
          },
        ]
      : []),
  ];

  const badgeContent = message({ id: redemptionDetails?.status });

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "rewardsFulfillment" }),
      path: links.rewardFulfillmentsList,
    },
  };

  return (
    <>
      <TopHeader
        title={redemptionDetails ? redemptionDetails?.name : ""}
        clearLink={links.rewardFulfillmentsList}
        breadcrumbs={breadcrumbs}
        badge={<StatusValue label={badgeContent} />}
      />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderActionButton
            text={message({ id: "changeStatus" })}
            onClick={() => setOpen(true)}
            variant="contained"
            data-test-id="single-reward-fulfillment-change-status-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <Grid
        container
        className={`${classes.container} ${classes.bottomSpace}`}
        spacing={10}
      >
        <Grid className={classes.gridChild} item xs={12} sm={12} md={3}>
          <OLPaper className={classes.detailsPaper}>
            <Box className={classes.mainDataContainer}>
              {rewardData ? (
                <Avatar
                  alt={rewardData.name}
                  className={classes.avatar}
                  variant="square"
                >
                  {getAcronym(rewardData.name)}
                </Avatar>
              ) : (
                <Skeleton height={48} width={180} />
              )}
              <Box className={classes.overflowText} ref={observeElement}>
                <Tooltip
                  text={rewardData?.name}
                  disableHoverListener={!isOverflown}
                  placement="bottom-start"
                >
                  <span className={classes.largeText}>
                    {rewardData ? rewardData.name : <Skeleton width={100} />}
                  </span>
                </Tooltip>
                <Typography className={classes.smallText}>
                  {rewardData ? (
                    `${message({
                      id:
                        rewardData?.couponValueType === "percentage"
                          ? "percentageCoupon"
                          : rewardData?.reward,
                    })} ${message({ id: "reward" }).toLowerCase()}`
                  ) : (
                    <Skeleton width={100} />
                  )}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.dataContainer}>
              {details ? (
                details.map(
                  (
                    item: {
                      label: string;
                      value: string;
                      dataHjSuppress?: boolean;
                    },
                    index: number,
                  ) => (
                    <Box
                      key={index}
                      data-test-id={`single-reward-fulfillment-${item.label}`}
                    >
                      <Typography className={classes.smallText}>
                        {message({ id: item.label })}
                      </Typography>
                      <Typography
                        className={classes.largeText}
                        data-hj-suppress={item.dataHjSuppress}
                      >
                        {redemptionDetails ? (
                          item.value || "-"
                        ) : (
                          <Skeleton width={100} />
                        )}
                      </Typography>
                    </Box>
                  ),
                )
              ) : (
                <Skeleton width={100} />
              )}
            </Box>
          </OLPaper>
        </Grid>
        <Grid item xs={12} sm={12} md={9}>
          <Grid item xs={12}>
            <RewardFulfillmentAddressCard
              isLoading={!redemptionDetails}
              address={redemptionDetails?.customerData?.address}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.sectionHeader}>
              {message({ id: "redemptionHistory" })}
            </Typography>
            <OLTable
              setColumnOrdering={setColumnOrdering}
              tableName={SINGLE_REWARD_FULFILLMENTS_ID}
              data-test-id="single-reward-fulfillment"
              columns={headItems}
              onSort={() => {}}
              rows={redemptionHistory && items}
              sort={{
                order: listing.order,
                property: listing.property,
                rowsPerPage: listing.rowsPerPage,
                page: listing.page,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <ChangeRedemptionStatusModal
        closeModal={() => setOpen(false)}
        open={open}
        redemptionId={issuedRewardId as string}
      />
    </>
  );
}
